import React, { useEffect, useRef, useState } from "react";
import {
  TileComponentContainer,
  TileContainer,
  TileImageWrap,
  TileImage,
  TileLink,
  ProductContainer,
  ProductImages,
  ProductHoverImage,
  ProductDisplayImage,
  ProductSection,
} from "../styles/components/tiles";
import VideoMux from "./videoMux";
import animations from "../animations/animations";
import tileData from "../data/tileData";

const chunkArray = (array, chunkSize) => {
  const chunks = [];
  for (let i = 0; i < array.length; i += chunkSize) {
    chunks.push(array.slice(i, i + chunkSize));
  }
  return chunks;
};

const VideosTile = ({
  muxvideo,
  autoplay,
  muted,
  loop,
  section,
  count,
  chunkIndex,
}) => {
  const videoRef = useRef();

  const tileStyles = tileData
    .find(data => data.section === section)
    ?.tiles?.find(
      tile => tile.chunkIndex === chunkIndex && tile.index === count
    );

  return (
    <TileContainer
      ref={videoRef}
      $customStyles={tileStyles?.customStyles && tileStyles?.customStyles}
      $minWidth
      $maxWidth
    >
      {muxvideo && (
        <VideoMux
          muxvideo={muxvideo}
          autoplay={autoplay}
          muted={muted}
          loop={loop}
        />
      )}
    </TileContainer>
  );
};

const ProductTile = ({ images, section, count, chunkIndex }) => {
  const displayImage = images[0];
  const [currentImage, setCurrentImage] = useState(
    images[0]?.image?.fluid?.src || ""
  );
  const hoverImageRef = useRef();

  const { parallax, link } = displayImage;

  const tileStyles = tileData
    .find(data => data.section === section)
    ?.tiles?.find(
      tile => tile.chunkIndex === chunkIndex && tile.index === count
    );

  const handleHover = index => {
    setCurrentImage(images[index]?.image?.fluid?.src || currentImage);
  };

  useEffect(() => {
    if (parallax) {
      setTimeout(() => {
        animations.parallax(hoverImageRef.current, 30);
      }, 1000);
    }
  }, [images, parallax]);

  return (
    <>
      {link ? (
        <ProductContainer
          ref={hoverImageRef}
          $customStyles={tileStyles?.customStyles && tileStyles?.customStyles}
        >
          <TileLink href={link} target="_blank" rel="noreferrer">
            <ProductImages>
              <ProductDisplayImage src={currentImage} />
            </ProductImages>
            <ProductHoverImage>
              {images.map((_, index) => (
                <ProductSection
                  key={`hover-section-${index}`}
                  style={{ width: `${100 / images.length}%` }}
                  onMouseOver={() => handleHover(index)}
                />
              ))}
            </ProductHoverImage>
          </TileLink>
        </ProductContainer>
      ) : (
        <ProductContainer
          ref={hoverImageRef}
          $customStyles={tileStyles?.customStyles && tileStyles?.customStyles}
        >
          <ProductImages>
            <ProductDisplayImage src={currentImage} />
          </ProductImages>
          <ProductHoverImage>
            {images.map((_, index) => (
              <ProductSection
                key={`hover-section-${index}`}
                style={{ width: `${100 / images.length}%` }}
                onMouseOver={() => handleHover(index)}
              />
            ))}
          </ProductHoverImage>
        </ProductContainer>
      )}
    </>
  );
};

const Tile = ({
  type,
  image,
  imageMobile,
  parallax,
  parallaxNumber,
  link,
  section,
  count,
  chunkIndex,
}) => {
  const imageRef = useRef();

  const tileStyles = tileData
    .find(data => data.section === section)
    ?.tiles?.find(
      tile => tile.chunkIndex === chunkIndex && tile.index === count
    );

  useEffect(() => {
    if (parallax) {
      setTimeout(() => {
        animations.parallax(imageRef.current, parallaxNumber);
      }, 1000);
    }
  }, [image, parallax, parallaxNumber]);

  return (
    <>
      {link ? (
        <TileLink href={link} target="_blank" rel="noreferrer">
          <TileContainer
            ref={imageRef}
            $customStyles={tileStyles?.customStyles && tileStyles?.customStyles}
            $minWidth={type === "component--images"}
            $maxWidth={type === "component--articles"}
          >
            {image && <TileImage src={image.src} $desktop={!!imageMobile} />}
            {!!imageMobile && <TileImage src={imageMobile.src} $mobile />}
          </TileContainer>
        </TileLink>
      ) : (
        <TileContainer
          ref={imageRef}
          $customStyles={tileStyles?.customStyles && tileStyles?.customStyles}
          $minWidth={type === "component--images"}
          $maxWidth={type === "component--articles"}
        >
          {image && <TileImage src={image.src} $desktop={!!imageMobile} />}
          {!!imageMobile && <TileImage src={imageMobile.src} $mobile />}
        </TileContainer>
      )}
    </>
  );
};

const TileChunk = ({ chunk, chunkIndex }) => {
  return (
    <TileImageWrap>
      {chunk.map((tile, index) => {
        switch (tile.type) {
          case "component--video-mux":
            return (
              <VideosTile
                key={index}
                chunkIndex={chunkIndex}
                count={index}
                {...tile}
              />
            );
          case "component--products":
            return (
              <ProductTile
                key={index}
                chunkIndex={chunkIndex}
                count={index}
                {...tile}
              />
            );
          default:
            return (
              <Tile
                key={index}
                chunkIndex={chunkIndex}
                count={index}
                {...tile}
              />
            );
        }
      })}
    </TileImageWrap>
  );
};

const TileComponent = ({ section, content }) => {
  const tileContent = Object.values(content)
    .flatMap((component, componentIndex) => {
      if (component.moduleTitle === "component--products") {
        return {
          key: `${component.moduleTitle}-${componentIndex}`,
          type: component.moduleTitle,
          section: section,
          images: component.images,
        };
      } else if (component.moduleTitle === "component--video-mux") {
        return {
          key: `${component.moduleTitle}-${componentIndex}`,
          type: component.moduleTitle,
          section: section,
          muxvideo: component.muxvideo,
          autoplay: component.autoplay,
          muted: component.muted,
          loop: component.loop,
        };
      } else {
        if (component.images) {
          return component.images?.flatMap((image, index) =>
            [
              {
                key: `${component.moduleTitle}-${index}`,
                type: component.moduleTitle,
                section: section,
                image: image?.image?.fluid,
                imageMobile: image?.imageMobile?.fluid,
                parallax: !!image.parallax,
                parallaxNumber: image?.parallaxNumber || 30,
                link: image?.link,
              },
            ].filter(Boolean)
          );
        }
      }
    })
    .filter(Boolean);

  const tileCount = section === "stream" || section === "articles" ? 2 : 3;
  const chunkedTiles = chunkArray(tileContent, tileCount);

  return (
    <TileComponentContainer>
      {chunkedTiles.map((chunk, chunkIndex) => (
        <TileChunk key={chunkIndex} chunk={chunk} chunkIndex={chunkIndex} />
      ))}
    </TileComponentContainer>
  );
};

export default TileComponent;
